import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from './AccountContext';
import { getSession, getAutoToken, clearSession } from '../../../utils/authUtils';
import GMBService from '../../../services/GMBService';
import GMBWebsiteService from '../../../services/GMBWebsiteService';
import api from '../../../services/api';
import DashboardLayout from '../../Layouts/DashboardLayout';
import logger from '../../../utils/logger';
import './AccountInfo.css';

// Helper function to format full address from GMB address object
const formatFullAddress = (gmbAddress) => {
  if (!gmbAddress) return '';
  if (typeof gmbAddress === 'string') return gmbAddress;

  const parts = [];

  // Street address lines
  if (gmbAddress.addressLines && Array.isArray(gmbAddress.addressLines)) {
    parts.push(...gmbAddress.addressLines.filter(line => line));
  }

  // City/Locality
  if (gmbAddress.locality) {
    parts.push(gmbAddress.locality);
  }

  // State/Province and Postal Code
  if (gmbAddress.administrativeArea && gmbAddress.postalCode) {
    parts.push(`${gmbAddress.administrativeArea} ${gmbAddress.postalCode}`);
  } else if (gmbAddress.administrativeArea) {
    parts.push(gmbAddress.administrativeArea);
  } else if (gmbAddress.postalCode) {
    parts.push(gmbAddress.postalCode);
  }

  return parts.join(', ');
};

const AccountInfo = () => {
  const navigate = useNavigate();
  const { accountInfo, updateAccountInfo } = useAccount();
  const _session_now = getSession();
  const _gmbTitleSeed = _session_now?.gmbPrimaryLocationTitle || _session_now?.gmbPrimaryLocation?.title || _session_now?.gmbData?.locations?.[0]?.title || _session_now?.locations?.[0]?.title || _session_now?.gmbLocations?.[0]?.title || '';
  const _gmbAccountNameSeed = _session_now?.gmbPrimaryAccountName || _session_now?.gmbAccounts?.[0]?.accountName || _session_now?.user?.accountName || _session_now?.accountName || '';
  const [form, setForm] = useState({
    name: accountInfo.name || _gmbAccountNameSeed || '',
    business_name: accountInfo.business_name || _gmbTitleSeed || '',
    address: accountInfo.address || '',
    email: accountInfo.email || '',
    contact_number: accountInfo.contact_number || '',
    alternative_contact_number: accountInfo.alternative_contact_number || '',
    website: accountInfo.website || '',
  });

  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  useEffect(() => {
    try {
      const session = getSession();
      const user = session?.user || session || {};
      // Always allow access - removed GMB access restrictions
      const hasGMBAccess = true;
      logger.debug('[AccountInfo] GMB Access Debug: Always enabled (restrictions removed)');

      const sessionValues = {
        // Prefer GMB account-level accountName for Full Name
        name: user?.gmbPrimaryAccountName || session?.gmbPrimaryAccountName || user?.accountName || session?.accountName || (session?.gmbAccounts?.[0]?.accountName) || '',
        business_name: user?.business_name || user?.company || session?.business_name || session?.gmbPrimaryLocationTitle || session?.gmbPrimaryLocation?.title || session?.gmbData?.locations?.[0]?.title || session?.locations?.[0]?.title || session?.gmbLocations?.[0]?.title || '',
        address: user.address || session?.address || '',
        email: user.email || session?.email || '',
        contact_number: user.contact_number || user.phone || session?.phone || session?.contact_number || '',
        alternative_contact_number: user.alternative_contact_number || user.alt_phone || session?.alternative_contact_number || '',
        website: user.website || session?.website || ''
      };

      logger.debug('[AccountInfo] Session Data Debug:', {
        hasSession: !!session,
        sessionKeys: session ? Object.keys(session) : [],
        businessName: sessionValues.business_name,
        address: sessionValues.address,
        contactNumber: sessionValues.contact_number,
        name: sessionValues.name
      });

      setForm(prev => ({
        name: sessionValues.name || prev.name,
        business_name: sessionValues.business_name || prev.business_name,
        address: sessionValues.address || prev.address,
        email: sessionValues.email || prev.email,
        contact_number: sessionValues.contact_number || prev.contact_number,
        alternative_contact_number: sessionValues.alternative_contact_number || prev.alternative_contact_number,
        website: sessionValues.website || prev.website
      }));

      (async () => {
        try {
          const token = getAutoToken();
          logger.debug('[AccountInfo] Token check:', !!token ? `${token.substring(0, 10)}...` : 'No token');
          if (!token) {
            logger.debug('[AccountInfo] No token available, skipping GMB fetch');
            return;
          }
          // Removed GMB access check - always attempt to fetch
          logger.debug('[AccountInfo] Starting GMB accounts/locations fetch...');

          const accs = await GMBService.getAccounts(token);
          logger.debug('[AccountInfo] Fetched GMB accounts:', accs?.length || 0);
          setAccounts(accs || []);

          if (accs && accs.length > 0) {
            const locs = await GMBService.getLocations(token, accs[0].name);
            logger.debug('[AccountInfo] Fetched GMB locations:', locs?.length || 0, locs);
            setLocations(locs || []);

            // Auto-select first location if only one is available
            if (locs && locs.length === 1) {
              logger.debug('[AccountInfo] Auto-selecting single location');
              const loc = locs[0];
              const gmbAddress = loc?.storefrontAddress || loc?.address || {};
              setForm(prev => ({
                ...prev,
                business_name: loc.title || loc.name || prev.business_name,
                address: formatFullAddress(gmbAddress) || prev.address,
                contact_number: (loc.phoneNumbers?.primaryPhone || loc.primaryPhone) || prev.contact_number,
                website: loc.websiteUri || prev.website,
                name: accs[0]?.accountName || prev.name
              }));
              setSelectedLocation('0'); // Set to first location
            }
          }
        } catch (err) {
          logger.debug('[AccountInfo] GMB account/location fetch error', err);
        }
      })();

      // Fallback: Create business profile from session data if available
      if (sessionValues.business_name) {
        logger.debug('[AccountInfo] Creating fallback business profile from session data');
        const fallbackLocation = {
          title: sessionValues.business_name,
          name: sessionValues.business_name,
          storefrontAddress: sessionValues.address,
          websiteUri: sessionValues.website,
          phoneNumbers: { primaryPhone: sessionValues.contact_number }
        };
        setLocations([fallbackLocation]);
        setAccounts([{ accountName: sessionValues.name || 'Current User' }]);
      } else {
        // Create a generic profile if no business data exists
        logger.debug('[AccountInfo] No session business data - creating generic profile option');
        const genericLocation = {
          title: 'My Business',
          name: 'My Business',
          storefrontAddress: '',
          websiteUri: '',
          phoneNumbers: { primaryPhone: '' }
        };
        setLocations([genericLocation]);
        setAccounts([{ accountName: 'Current User' }]);
      }

      (async () => {
        try {
          const token = getAutoToken() || (typeof window !== 'undefined' && localStorage.getItem('googleAccessToken'));
          logger.debug('[AccountInfo][debug] token for GMB fetch:', !!token ? (token.substring ? token.substring(0, 8) + '...' : 'token') : token);
          logger.debug('[AccountInfo][debug] sessionValues:', sessionValues);
          const needGMB = (!sessionValues.business_name || !sessionValues.address || !sessionValues.contact_number) && !!token;
          logger.debug('[AccountInfo][debug] needGMB:', needGMB, 'conditions:', {
            noBusinessName: !sessionValues.business_name,
            noAddress: !sessionValues.address,
            noContactNumber: !sessionValues.contact_number,
            hasToken: !!token
          });
          if (!needGMB) {
            logger.debug('[AccountInfo][debug] GMB auto-fill not needed, skipping');
            return;
          }

          const primaryLocation = await GMBWebsiteService.getPrimaryLocation(token);
          logger.debug('[AccountInfo][debug] primaryLocation fetched:', primaryLocation);
          if (!primaryLocation) return;

          const gmbAddress = primaryLocation?.storefrontAddress || primaryLocation?.address || {};
          const gmbPhone = primaryLocation?.phoneNumbers?.primaryPhone || primaryLocation?.primaryPhone || '';
          const gmbName = primaryLocation?.title || primaryLocation?.name || '';

          setForm(prev => ({
            name: user?.gmbPrimaryAccountName || user?.accountName || session?.gmbPrimaryAccountName || session?.accountName || prev.name,
            business_name: gmbName || prev.business_name,
            address: formatFullAddress(gmbAddress) || prev.address,
            email: user?.email || prev.email,
            contact_number: gmbPhone || prev.contact_number,
            alternative_contact_number: prev.alternative_contact_number,
            website: primaryLocation?.websiteUri || prev.website
          }));
        } catch (err) {
          logger.debug('[AccountInfo] GMB autofill error', err);
        }
      })();
    } catch (err) {
      logger.debug('[AccountInfo] autofill error', err);
    }
  }, [accountInfo]);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSaveAndNext = async (e) => {
    e.preventDefault();
    updateAccountInfo(form);

    const session = getSession();
    const authToken = getAutoToken();

    // Check for authentication
    if (!session || !authToken) {
      logger.error('[AccountInfo] Missing authentication data');
      alert('Authentication required. Please log in again.');
      clearSession();
      navigate('/login');
      return;
    }

    // Extract user ID with multiple fallback strategies
    const userId = session?.id || session?.user?.id || session?.userId || session?.user_id;



    if (!userId) {
      logger.error('[AccountInfo] Unable to extract user ID from session:', session);
      alert('Invalid session data. Please log in again.');
      clearSession();
      navigate('/login');
      return;
    }

    const payload = {
      user_id: userId,
      name: form.name,
      business_name: form.business_name,
      address: form.address,
      email: form.email,
      contact_number: form.contact_number,
      alternative_contact_number: form.alternative_contact_number,
      website: form.website,
    };

    try {
      setLoading(true);


      // Send with authentication headers for external EDMS API

      const response = await api.post('/customer/edms/create-edms', payload);
      logger.log('[AccountInfo] EDMS created successfully');
      navigate('../select-design');
    } catch (error) {
      logger.error('[AccountInfo] API Error:', error);

      if (error.status === 401) {
        // Handle authentication error
        logger.log('[AccountInfo] Authentication failed, clearing session');
        clearSession();
        alert('Your session has expired. Please log in again.');
        navigate('/login');
      } else if (error.status === 400) {
        // Handle bad request with specific feedback
        const errorMessage = error.body?.message || error.body?.error || 'Invalid request data. Please check your information and try again.';
        logger.error('[AccountInfo] Validation error:', errorMessage);
        alert(`Error: ${errorMessage}`);
      } else {
        logger.error('[AccountInfo] Unexpected error:', error.message);
        alert('Something went wrong while submitting the form. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  const handleCancel = () => {
    logger.log('Cancel button clicked');
    try {
      navigate(0);
      logger.log('Navigation successful');
    } catch (error) {
      logger.error('Navigation error:', error);
    }
  };

  return (
    <div className="container">
      <h2>Account Information</h2>
      <h6>Fill in your account details or select a Google Business Profile to auto-fill the form.</h6>
      <div className="input-container" style={{ marginBottom: '20px' }}>
        <label>Business Profile</label>
        <select
          name="selectedLocation"
          value={selectedLocation}
          onChange={(e) => {
            const index = e.target.value;
            setSelectedLocation(index);
            if (index !== '') {
              const loc = locations[index];
              if (loc) {
                const gmbAddress = loc?.storefrontAddress || loc?.address || {};
                setForm(prev => ({
                  ...prev,
                  business_name: loc.title || loc.name || '',
                  address: formatFullAddress(gmbAddress) || '',
                  contact_number: (loc.phoneNumbers?.primaryPhone || loc.primaryPhone) || '',
                  website: loc.websiteUri || '',
                  name: accounts[0]?.accountName || prev.name
                }));
              }
            }
          }}
          className="input-style-for-select"
        >
          <option value="">Select a Business Profile to auto-fill</option>
          {locations.map((loc, idx) => (
            <option key={idx} value={idx}>{loc.title || loc.name}</option>
          ))}
        </select>
      </div>
      <form onSubmit={handleSaveAndNext}>
        <div className="form-fields-column">
          <div className="input-container">
            <label>Full Name <span className="required">*</span></label>
            <input type="text" name="name" placeholder="Enter your full name" value={form.name} onChange={handleChange} required />
          </div>
          <div className="input-container">
            <label>Business Name <span className="required">*</span></label>
            <input type="text" name="business_name" placeholder="Enter your business name" value={form.business_name} onChange={handleChange} required />
          </div>
          <div className="input-container">
            <label>Business Address <span className="required">*</span></label>
            <input type="text" name="address" placeholder="Enter your business address" value={form.address} onChange={handleChange} required />
          </div>
          <div className="input-container">
            <label>Email Address <span className="required">*</span></label>
            <input type="email" name="email" placeholder="Enter your email address" value={form.email} onChange={handleChange} required />
          </div>
          <div className="input-container">
            <label>Contact Number</label>
            <input type="tel" name="contact_number" placeholder="Enter your contact number" value={form.contact_number} onChange={handleChange} />
          </div>
          <div className="input-container">
            <label>Alternative Contact Number</label>
            <input type="tel" name="alternative_contact_number" placeholder="Enter your alternative contact number" value={form.alternative_contact_number} onChange={handleChange} />
          </div>
          <div className="input-container">
            <label>Website URL</label>
            <input type="url" name="website" placeholder="https://www.yourwebsite.com" value={form.website} onChange={handleChange} />
          </div>
          <div className="buttons">
            <button
              type="button"
              className="btn cancel"
              onClick={handleCancel}
              style={{ cursor: 'pointer', zIndex: 1000 }}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn save"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save & Next'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AccountInfo;
